import { action, computed, observable } from 'mobx'
import type { DependentRelationship } from '@taxfyle/txf-api-client'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { validationContext, required } from 'validx'
import {
  mustBeValidPersonalTaxpayerIdentificationNumber,
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  mustBeInThePast,
} from '@/utils/validx-util'
import {
  type Dependent,
  type DependentProps,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/models'
import { DEPENDENT_RELATIONSHIPS_MAP } from '../../../data/dependent-relationships'
import { formatFullName } from '@/utils/full-name-util'

export class DependentAccordionViewModel implements ActivationLifecycle {
  readonly lifecycle = new LifecycleTracker()

  @observable accessor isExpanded = false

  @observable accessor firstName = ''
  @observable accessor middleInitial = ''
  @observable accessor lastName = ''
  @observable accessor tin = ''
  @observable accessor dateOfBirth: Date | null = null
  @observable accessor relationship: DependentRelationship | null = null

  readonly validation = validationContext(this, {
    firstName: [
      required('First name is required'),
      mustBeValidTaxFormInputValue(),
    ],
    lastName: [
      required('Last name is required'),
      mustBeValidTaxFormInputValue(),
    ],
    middleInitial: [
      mustHaveMaxLength(1, 'Middle initial must be a single character'),
      mustBeValidTaxFormInputValue(),
    ],
    tin: [
      required('SSN is required'),
      mustBeValidPersonalTaxpayerIdentificationNumber(),
    ],
    dateOfBirth: [
      required('Date of birth is required'),
      mustBeInThePast('Date of birth must be in the past'),
    ],
    relationship: [required('Relationship is required')],
  })

  constructor(private readonly _model: Dependent) {
    this.syncFromModel()
  }

  @computed
  get id() {
    return this._model.id
  }

  @computed
  get fullName() {
    return formatFullName(this.firstName, this.middleInitial, this.lastName)
  }

  @computed
  get displayName() {
    return this.fullName || 'Dependent'
  }

  @computed
  get displaySubtitle() {
    return this.relationship
      ? DEPENDENT_RELATIONSHIPS_MAP[this.relationship]
      : null
  }

  toProps(): DependentProps {
    return {
      id: this._model.id,
      firstName: this.firstName || null,
      middleInitial: this.middleInitial || null,
      lastName: this.lastName || null,
      tin: this.tin || null,
      dob: this.dateOfBirth,
      relationship: this.relationship,
      archived: this._model.archived,
    }
  }

  @action.bound
  toggleExpanded() {
    this.isExpanded = !this.isExpanded
  }

  @action.bound
  expand() {
    this.isExpanded = true
  }

  @action.bound
  syncFromModel() {
    const model = this._model
    this.firstName = model.firstName ?? ''
    this.middleInitial = model.middleInitial ?? ''
    this.lastName = model.lastName ?? ''
    this.tin = model.tin ?? ''
    this.dateOfBirth = model.dob
    this.relationship = model.relationship

    this.validation.reset()
  }

  activate() {
    this.lifecycle.activate()
  }

  deactivate() {
    this.lifecycle.deactivate()
  }
}
